import { useEffect, useRef, useState } from "react";
import type { Lang } from "../lib/i18n";
import type { Theme } from "../lib/state";

const MAX_INTRO_MS = 5000; // auto-advance if the video never ends
const PLAY_CHECK_MS = 1200; // fall back to the static logo if playback hasn't started

/** Intro overlay (YP) — plays a short brand video, with a static-logo fallback and skip. */
export function Splash({ theme, lang, onDone }: { theme: Theme; lang: Lang; onDone: () => void }) {
  const [closing, setClosing] = useState(false);
  const [useFallback, setUseFallback] = useState(false);
  const doneRef = useRef(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const finish = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    setClosing(true);
    window.setTimeout(() => onDone(), 240);
  };

  useEffect(() => {
    const autoTimer = window.setTimeout(finish, MAX_INTRO_MS);
    const playCheck = window.setTimeout(() => {
      const v = videoRef.current;
      if (!(v && (v.currentTime > 0 || v.readyState >= 3))) setUseFallback(true);
    }, PLAY_CHECK_MS);
    videoRef.current?.play().catch(() => setUseFallback(true));
    return () => {
      window.clearTimeout(autoTimer);
      window.clearTimeout(playCheck);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const src = theme === "light" ? "/intro/logo_light.mp4" : "/intro/logo_dark.mp4";

  return (
    <div className={`kd-intro-overlay${closing ? " is-closing" : ""}`} role="dialog" aria-label={lang === "ar" ? "مقدمة كادن" : "Kaden intro"} onClick={finish}>
      <video
        ref={videoRef}
        className="kd-intro-video"
        src={src}
        autoPlay
        muted
        playsInline
        preload="auto"
        style={useFallback ? { display: "none" } : undefined}
        onEnded={finish}
        onError={() => setUseFallback(true)}
      />
      {useFallback && (
        <div className="kd-intro-fallback">
          <img src={theme === "light" ? "/logo_light.svg" : "/logo_dark.svg"} alt="Kaden Investment" className="kd-intro-fallback-logo" />
        </div>
      )}
      <button
        type="button"
        className="kd-intro-skip"
        onClick={(e) => {
          e.stopPropagation();
          finish();
        }}
      >
        {lang === "ar" ? "تخطي ←" : "Skip ›"}
      </button>
    </div>
  );
}
